<script setup>
import { Link, router, usePage, usePoll } from '@inertiajs/vue3'
import { computed, ref, watch } from 'vue'
import { useToast } from 'primevue/usetoast'
import NotificationBell from './NotificationBell.vue'

const page = usePage()
const toast = useToast()

// Poll shared props every 60s — picks up deployment alerts without page navigation
usePoll(60_000, { only: ['systemAlerts'] })

const user = computed(() => page.props.auth?.user)
const can = computed(() => page.props.auth?.can)
const isDrawerVisible = ref(false)

// Deployment alerts show as a bottom-right toast (below); the bell itself
// lives in <NotificationBell />, which reads systemAlerts + unread count.
const allAlerts = computed(() => page.props.systemAlerts ?? [])

// Show deployment alert as a bottom-right toast (once per alert cycle)
const deploymentToastShown = ref(false)
watch(allAlerts, (alerts) => {
  const deployAlert = alerts.find(a => a.key === 'deployment')
  if (deployAlert && !deploymentToastShown.value) {
    toast.add({
      group: 'deployment',
      severity: 'info',
      summary: 'Süsteemi uuendus',
      detail: deployAlert.message,
      life: 30000,
    })
    deploymentToastShown.value = true
  }
  if (!deployAlert) {
    deploymentToastShown.value = false
  }
}, { immediate: true })

const mainNavLinks = computed(() =>
  [
    {
      href: '/',
      label: 'messages.headerTitle',
      visible: true,
      prefetch: false,
    },
    {
      href: route('admin.index'),
      label: 'messages.headerAdmin',
      visible: user.value && can.value.admin,
      prefetch: true,
    },
    {
      href: route('admin.real-estate.index'),
      label: 'messages.headerRealEstate',
      visible: user.value && can.value.admin,
      prefetch: false,
    },
    {
      href: route('upload.index'),
      label: 'messages.headerUpload',
      visible: user.value && can.value.create,
      prefetch: false,
    },
    {
      href: '/transactions',
      label: 'messages.headerTransactions',
      visible: user.value && can.value.view,
      prefetch: true,
    },
    {
      href: route('leaderboard'),
      label: 'messages.headerLeaderboard',
      visible: user.value && can.value.leaderboard,
      prefetch: false,
    },
  ].filter(l => l.visible),
)

const profileMenu = ref(null)
const items = ref([
  {
    label: 'Menüü',
    items: [
      {
        label: 'Profiil',
        icon: 'pi pi-user',
        command: () => {
          router.get(route('profile'))
        },
      },
      {
        label: 'Logi välja',
        icon: 'pi pi-sign-out',
        command: () => {
          router.post(route('logout'))
        },
      },
    ],
  },
])

const toggleMenu = event => {
  profileMenu.value.toggle(event)
}
</script>

<template>
  <div class="bg-gray-600">

    <div class="container mx-auto px-4">
        <div class="flex items-center justify-between h-16">
            <!-- Mobile menu button -->
            <button
                class="md:hidden flex items-center p-2 rounded-md text-white hover:text-gray-900 hover:bg-gray-100 focus:outline-hidden"
                type="button"
                data-toggle="collapse"
                data-target="#navbarToggler1"
                aria-controls="navbarTogglerDemo01"
                aria-expanded="false"
                aria-label="Toggle navigation"
                @click="isDrawerVisible = true"
            >
                <svg class="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
            </button>

            <!-- Desktop menu -->
            <div class="hidden md:flex md:items-center md:justify-between w-full" id="navbarToggler1">
                <!-- Left side nav links -->
                <ul class="flex flex-col items-center md:flex-row space-y-2 md:space-y-0 md:space-x-4">
                    <li v-for="link in mainNavLinks" :key="link.href" class="font-medium">
                        <Link class="text-white hover:text-gray-400" :href="link.href" :prefetch="link.prefetch">{{ $t(link.label) }}</Link>
                    </li>

                    <!-- Quick Valuation Button -->
                    <div v-if="can['quick-valuation']" class="flex justify-center">
                        <Link
                            href="/quick-valuation"
                            class="inline-flex items-center gap-1 px-2 py-1 bg-linear-to-r from-emerald-500 to-teal-600 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl hover:from-emerald-600 hover:to-teal-700 transform hover:-translate-y-0.5 transition-all duration-200"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                            <span>Kiirhindamine</span>
                        </Link>
                    </div>
                </ul>

                <!-- Right side user menu -->
                <div v-if="!user" class="flex flex-col md:flex-row space-y-2 md:space-y-0 md:space-x-4 list-none">
                    <li class="font-medium">
                        <Link class="text-white hover:text-gray-400" :href="route('login')">{{ $t('messages.headerLogin') }}</Link>
                    </li>

                    <li v-if="false" class="font-medium">
                        <a class="text-gray-700 hover:text-gray-400" :href="route('register')">{{ __('messages.Register') }}</a>
                    </li>
                </div>
                <div v-else class="relative inline-block text-left">
                  <div class="flex gap-2 items-center">
                    <div class="flex items-center gap-2 cursor-pointer bg-gray-700 rounded-md py-2 px-4 hover:bg-gray-800" @click="toggleMenu">
                      <span class="pi pi-user text-white" />
                      <span class="text-white">{{ user.firstname }}</span>
                    </div>
                    <Menu ref="profileMenu" id="profileMenu" :model="items" popup />
                    <NotificationBell />
                  </div>
                </div>

            </div>
        </div>
    </div>
  </div>

    <Drawer v-model:visible="isDrawerVisible" header="Menüü" position="left">
        <div class="flex flex-col space-y-4">
            <ul class="flex flex-col space-y-2">
                <li v-for="link in mainNavLinks" :key="link.href" class="font-medium">
                    <Link class="text-gray-700 hover:text-gray-900" :href="link.href" :prefetch="link.prefetch" @click="isDrawerVisible = false">{{ $t(link.label) }}</Link>
                </li>
                <li v-if="can['quick-valuation']">
                    <Link
                        href="/quick-valuation"
                        class="inline-flex items-center gap-1 px-2 py-1 bg-linear-to-r from-emerald-500 to-teal-600 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl hover:from-emerald-600 hover:to-teal-700 transition-all duration-200"
                        @click="isDrawerVisible = false"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                        <span>Kiirhindamine</span>
                    </Link>
                </li>
            </ul>

            <div class="border-t border-gray-200 pt-4">
                <div v-if="!user" class="flex flex-col space-y-2 list-none">
                    <li class="font-medium">
                        <Link class="text-gray-700 hover:text-gray-900" :href="route('login')">{{ $t('messages.headerLogin') }}</Link>
                    </li>

                    <li v-if="false" class="font-medium">
                        <a class="text-gray-700 hover:text-gray-900" :href="route('register')">{{ $t('messages.Register') }}</a>
                    </li>
                </div>
                <div v-else class="flex flex-col space-y-2">
                     <Link class="flex items-center gap-2 text-gray-700 hover:text-gray-900" :href="route('profile')">
                        <span class="pi pi-user" />
                        <span>Profiil</span>
                    </Link>
                    <button class="flex items-center gap-2 text-gray-700 hover:text-gray-900 text-left" @click="router.post(route('logout'))">
                        <span class="pi pi-sign-out" />
                        <span>Logout</span>
                    </button>
                </div>
            </div>
        </div>
    </Drawer>

    <Toast position="bottom-right" group="deployment" />
</template>
